import React from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CTABanner from "@/components/sections/CTABanner";
import LeadFormCompact from "@/components/sections/LeadFormCompact";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO, { courseSchema, faqSchema, breadcrumbSchema } from "@/components/SEO";
import { COURSES } from "@/site";

const piano = COURSES.find((c) => c.slug === "piano");

const FAQS = [
    {
        q: "What is the right age to start learning piano?",
        a: "Students as young as 5 can begin with our kids-focused programme. That said, piano is an instrument you can start at any age — we have adult beginners in their 40s, 50s and 60s happily progressing through the syllabus.",
    },
    {
        q: "How long does it take to learn piano?",
        a: "With consistent practice, most students clear ABRSM/Trinity Grade 1 within 8–12 months. Grades 2–5 typically take 1–2 years each. Progress is never about speed — it is about depth and confidence.",
    },
    {
        q: "How much daily practice is required?",
        a: "For beginners, 20–30 minutes, 5 days a week is ideal. Intermediate students should aim for 45–60 minutes daily. We provide structured weekly practice plans so you always know what to do.",
    },
    {
        q: "Do I need a piano at home?",
        a: "Yes — either an acoustic piano or a weighted-key digital piano (88 keys). We can guide you to budget-friendly options if you are unsure what to buy.",
    },
    {
        q: "Will I get to perform?",
        a: "Absolutely. Every term we host recitals and concerts where students perform for friends, family and the Ihao community. Performing is a core part of our curriculum.",
    },
];

const BENEFITS = [
    "Sharper memory, focus and hand-eye coordination",
    "Solid foundation in music theory that transfers to any instrument",
    "Performance confidence and stage presence",
    "A lifelong creative outlet and stress-buster",
];

export default function PianoCourse() {
    const schema = [
        courseSchema(piano),
        faqSchema(FAQS),
        breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Courses", path: "/courses" },
            { name: "Piano", path: "/courses/piano" },
        ]),
    ];
    return (
        <div data-testid="page-piano">
            <SEO title={piano.title} description={piano.metaDesc} path="/courses/piano" image={piano.image} schema={schema} />
            <Breadcrumbs items={[
                { label: "Home", to: "/" },
                { label: "Courses", to: "/courses" },
                { label: "Piano" },
            ]} />
            {/* HERO */}
            <section className="relative min-h-[80vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${piano.hero})` }} aria-hidden />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black" aria-hidden />

                <div className="relative max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-24 md:py-32">
                    <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold mb-6 border border-gold/30 rounded-full px-4 py-1.5 bg-black/30 backdrop-blur">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                        Limited Seats Available
                    </span>
                    <h1 className="font-display font-bold text-white text-balance leading-[1.05] text-4xl sm:text-5xl md:text-6xl">
                        Piano Classes in <span className="text-gold">Malviya Nagar, Delhi</span>{" "}
                        <span className="block text-white/60 text-3xl md:text-4xl font-light mt-2">Near Saket</span>
                    </h1>
                    <p className="mt-6 text-white/75 text-base md:text-lg max-w-2xl leading-relaxed">
                        Learn piano from certified instructors following <strong className="text-white">Rockschool</strong>, <strong className="text-white">ABRSM</strong> and <strong className="text-white">Trinity</strong> syllabi — in South Delhi's most inspiring boutique studio.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-3">
                        <Link to="/contact" data-testid="piano-hero-book-demo" className="inline-flex items-center justify-center bg-gold text-black font-semibold text-sm px-8 py-4 rounded-full hover:scale-[1.03] hover:bg-gold-hover transition-all duration-300 gold-glow-strong">
                            Book Free Trial Class
                        </Link>
                        <a href="tel:+918800747721" className="inline-flex items-center justify-center border border-white/25 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/5 transition-colors">
                            Call +91 88007 47721
                        </a>
                    </div>
                </div>
            </section>

            {/* INTRO */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">The Course</span>
                    <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">
                        The best way to learn piano in South Delhi.
                    </h2>
                    <p className="mt-6 text-white/65 leading-relaxed">
                        Whether you are a complete beginner or preparing for a grade exam, our piano classes in Malviya Nagar near Saket give you a structured, joyful path forward. Weekly one-on-one lessons, written practice plans, performance opportunities — and a beautiful acoustic piano to learn on.
                    </p>
                </div>
            </section>

            {/* WHO IT'S FOR */}
            <section className="relative py-20 md:py-28 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-12">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Who it's for</span>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">Every kind of learner.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            { t: "Kids (5 – 12)", b: "Fun, song-based introduction to reading, rhythm and keyboard geography." },
                            { t: "Teens & Adults", b: "Grade-track or leisure-track — we build around your goal and schedule." },
                            { t: "Advanced Students", b: "Diploma preparation, repertoire building and performance masterclasses." },
                        ].map((x) => (
                            <div key={x.t} className="rounded-2xl border border-white/10 bg-[#121212] p-8 hover:border-gold/40 transition-colors">
                                <h3 className="font-display text-xl text-white font-semibold">{x.t}</h3>
                                <p className="mt-3 text-white/60 text-sm leading-relaxed">{x.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT YOU LEARN */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                    <img src={piano.image} alt="Piano class in Malviya Nagar" className="rounded-3xl border border-white/10 w-full" />
                    <div>
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Curriculum</span>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">What students learn.</h2>
                        <ul className="mt-8 space-y-4 text-white/75">
                            {[
                                "Correct technique, posture and hand position",
                                "Music reading — treble & bass clef, sight-reading",
                                "Scales, arpeggios and chord theory",
                                "Repertoire — classical, jazz, film music, contemporary",
                                "Ear training and improvisation basics",
                                "Grade exam preparation — ABRSM, Trinity, Rockschool",
                            ].map((p) => (
                                <li key={p} className="flex gap-3 text-sm"><span className="text-gold mt-1">—</span>{p}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* SYLLABUS / PERFORMANCE */}
            <section className="relative py-20 md:py-28 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="rounded-3xl border border-gold/30 p-10 bg-gradient-to-br from-[#1a1405] to-[#0a0a0a]">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold">International Syllabus</span>
                        <h3 className="mt-3 font-display text-2xl md:text-3xl text-white font-semibold">Rockschool · ABRSM · Trinity</h3>
                        <p className="mt-4 text-white/65 leading-relaxed">Exam-ready preparation for the world's most respected music boards, delivered by certified faculty.</p>
                    </div>
                    <div className="rounded-3xl border border-white/10 p-10 bg-[#121212]">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Performance Opportunities</span>
                        <h3 className="mt-3 font-display text-2xl md:text-3xl text-white font-semibold">Termly recitals & annual concerts</h3>
                        <p className="mt-4 text-white/65 leading-relaxed">Every student performs on stage at least 2–3 times a year — with audience, lights and a real piano.</p>
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Why Piano</span>
                    <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">Benefits that last a lifetime.</h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {BENEFITS.map((b, i) => (
                            <div key={i} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-[#121212] p-6">
                                <span className="text-gold font-display text-3xl font-light">{String(i + 1).padStart(2, "0")}</span>
                                <p className="text-white/75 text-sm leading-relaxed">{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LOCATION */}
            <section className="relative py-20 bg-background">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Location</span>
                    <h2 className="mt-3 font-display text-2xl md:text-4xl font-semibold text-white leading-tight">
                        Right in the heart of <span className="text-gold">Saket, South Delhi</span>.
                    </h2>
                    <p className="mt-4 text-white/60">
                        Opposite DLF Avenue Saket — easily accessible from Malviya Nagar, Hauz Khas, Green Park, Greater Kailash and GK-II.
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">FAQ</span>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">Frequently asked questions.</h2>
                    </div>

                    <Accordion type="single" collapsible className="space-y-3">
                        {FAQS.map((f, i) => (
                            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border border-white/10 rounded-2xl bg-[#121212] px-6">
                                <AccordionTrigger className="text-white font-display text-base md:text-lg font-semibold text-left hover:no-underline py-5">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-white/65 leading-relaxed pb-5">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            <LeadFormCompact />

            <CTABanner title="Start playing your first song this week." subtitle="Our free demo includes a 30-minute piano lesson with a certified instructor. Walk in curious, walk out playing." />
        </div>
    );
}
